import { useEffect, useState, useCallback } from "react";
import { Tabs, Tab } from "@mui/material";
import {
  getBrokerHoldingBook,
  getBrokerHoldingBookColumns,
} from "../../api/brokerBooks";
import CustomTable from "../../components/CustomTable/CustomTable";
import BookRefreshBar from "../../components/BookRefreshBar/BookRefreshBar";
import BookTableColumnPrefsInfo from "../../components/BookTableColumnPrefsInfo/BookTableColumnPrefsInfo";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import BookSquareOffAddMoreFlow from "../../components/BookSquareOffAddMoreFlow/BookSquareOffAddMoreFlow";
import { extractColumnsConfig, resolveBookColumns } from "../../utils/brokerBookColumns";
import { TABLE_COLUMN_TABLE_IDS } from "../../utils/tableColumnPreferences";
import { useTableColumnPreferencesVersion } from "../../hooks/useTableColumnPreferences";
import styles from "./HoldingBookPage.module.css";

function HoldingBookPage() {
  const [holdingBook, setHoldingBook] = useState([]);
  const [apiColumns, setApiColumns] = useState([]);
  useTableColumnPreferencesVersion(TABLE_COLUMN_TABLE_IDS.HOLDING_BOOK);
  const columnsConfig = resolveBookColumns(apiColumns, TABLE_COLUMN_TABLE_IDS.HOLDING_BOOK);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastRefreshAt, setLastRefreshAt] = useState(null);

  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const response = await getBrokerHoldingBookColumns();
        const columns = extractColumnsConfig(response);
        if (columns && columns.length > 0) {
          setApiColumns(columns);
        }
      } catch (err) {
        const errorMessage = err?.response?.data?.detail || err.message || "Failed to fetch columns configuration";
        console.error("Failed to fetch columns config:", errorMessage);
        setError(errorMessage);
      }
    };

    fetchColumns();
  }, []);

  const fetchHoldingBook = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const data = await getBrokerHoldingBook();
      setHoldingBook(data["book_rows"] || []);
      setLastRefreshAt(Date.now());
    } catch (err) {
      const errorMessage = err?.response?.data?.detail || err.message || "Failed to fetch holding book data";
      setError(errorMessage);
      setHoldingBook([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchHoldingBook();
  }, [fetchHoldingBook]);

  const handleRowSelect = useCallback((selectedIds, rowsData) => {
    setSelectedRows(selectedIds);
    setSelectedRowsData(rowsData || []);
  }, []);

  const handleExport = useCallback(() => {}, []);

  return (
    <div className={styles["container"]}>
      <div className={styles["primary-tabs-container"]}>
        <Tabs value="holdings" className={styles["primary-tabs"]}>
          <Tab
            icon={<GlobalIcon iconName="HoldingBookIcon" className={styles["tab-icon"]} />}
            iconPosition="start"
            label="Holdings"
            value="holdings"
            className={styles["primary-tab"]}
            disableRipple
          />
        </Tabs>
      </div>

      <div className={styles["content-wrapper"]}>
        <BookSquareOffAddMoreFlow
          bookType="holding"
          selectedRowsData={selectedRowsData}
          selectedCount={selectedRows.length}
          buttonClassName={styles["action-button"]}
          containerClassName={styles["action-buttons-container"]}
        />

        {error && <div className={styles["error-message"]}>{error}</div>}

        <div className={styles["table-wrapper"]}>
          {isLoading && lastRefreshAt === null ? (
            <div className={styles["loading-message"]}>Loading...</div>
          ) : (
            <CustomTable
              columns={columnsConfig}
              data={holdingBook}
              onRowSelect={handleRowSelect}
              selectedRows={selectedRows}
              rowKey="key"
              enableSearch={true}
              enableSorting={true}
              enableSelection={true}
              enableShowEntries={true}
              enableFilter={false}
              enableExport={true}
              enablePagination={true}
              searchPlaceholder="Search"
              onExport={handleExport}
              exportType="Holdings"
              columnsWithDropdown={["broker_client_code", "exchange_trading_symbol"]}
              toolbarRefresh={
                <>
                  <BookTableColumnPrefsInfo />
                  <BookRefreshBar
                    lastRefreshAt={lastRefreshAt}
                    onReload={() => void fetchHoldingBook()}
                    isLoading={isLoading}
                  />
                </>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default HoldingBookPage;
